import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FileDownload } from '@mui/icons-material';
import { Button, Fade, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  PortalPermissions,
  useEstate_GetEstateByIdQuery,
  useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery,
  useEstateShareMembership_GetEstateShareMembershipQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import EstateBalanceSheetDashboardHeader from 'pages/common/estate-balance-sheet/estate-balance-sheet-dashboard-header';
import EstateBalanceSheetDashboardMenu from 'pages/common/estate-balance-sheet/estate-balance-sheet-dashboard-menu';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import AccountSummaryTableComponent, {
  AccountSummaryTableRow,
} from './components/account-summary-table-component';
import EditAccountSummaryFieldModal from './components/edit-account-summary-field-modal';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  pill: {
    display: 'inline-block',
    background: '#F9F9F9',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
  },
  suggestionsLink: {
    fontSize: '12px',
    marginTop: '4px',
    color: '#1C2541',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '48px',
  },
  subTitle: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '32px',
  },
  badges: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  subTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  downloadButton: {
    background: 'transparent',
    border: '1px solid #C4C4C4',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1C2541',
  },
}));

const convertToCSV = (rows: AccountSummaryTableRow[], title: string) => {
  let csvStr = `${title}\r\nDescription,Current Value,Starting Value\r\n`;
  rows.forEach((row) => {
    let line = [row.label, row.currentValue, row.startingValue].join(',');
    csvStr += `${line}\r\n`;
  });

  return csvStr;
};

const downloadCSV = (data: {
  principalRows: AccountSummaryTableRow[];
  incomeRows: AccountSummaryTableRow[];
  totalsRows: AccountSummaryTableRow[];
}) => {
  try {
    const csvContent = [
      convertToCSV(data.principalRows, 'Principal'),
      convertToCSV(data.incomeRows, 'Income'),
      convertToCSV(data.totalsRows, 'Total'),
    ].join('\r\n\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'estate-accounting-summary.csv';
    link.click();
  } catch (error) {
    showAlert({
      message: 'Error downloading CSV',
      severity: NotificationSeverity.ERROR,
    });
  }
};

const EstateAccountSummaryPage = () => {
  const history = useHistory();
  const classes = useStyles();
  const { estateId, portalId } = useParams<Record<string, string>>();
  const [showEditModalRow, setShowEditModalRow] = useState<AccountSummaryTableRow>();

  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: { id: estateId },
    onError: (e) =>
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const { data: estateBalanceSheetData, loading: estateBalanceSheetLoading } =
    useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) =>
        showAlert({
          message: 'Error getting balance sheet by estate ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const { data: estateShareMembershipData, loading: estateShareMembershipLoading } =
    useEstateShareMembership_GetEstateShareMembershipQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate share membership: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (
    estateLoading ||
    !estateData ||
    estateBalanceSheetLoading ||
    !estateBalanceSheetData?.data ||
    estateShareMembershipLoading ||
    !estateShareMembershipData
  ) {
    return <Spinner />;
  }

  if (hasPortalPermissionClient && !estateShareMembershipData.data?.isActive) {
    // If shared client user, send to home page if they don't have access to this estate
    history.replace(`/home/${portalId}/home`);
    return <Spinner />;
  }

  const estate = estateData.data;
  const estateBalanceSheet = estateBalanceSheetData.data;
  const principalRows: AccountSummaryTableRow[] = [
    {
      label: 'Receipts',
      currentValue: null,
      startingValue: estateBalanceSheetData.data?.principalInitialValueReceipts,
    },
    {
      label: 'Net Gain/Loss from Sales',
      currentValue: null,
      startingValue: estateBalanceSheetData.data?.principalNetGainOrLossesFromSales,
      editColumnName: 'principalNetGainOrLossesFromSales',
    },
    {
      label: 'Total',
      currentValue: null,
      startingValue: estateBalanceSheetData.data?.totalPrincipal,
    },
    {
      label: '',
      currentValue: null,
      startingValue: null,
    },
    {
      label: 'Less Disbursements',
      currentValue: null,
      startingValue: null,
    },
    {
      label: 'Debts of Decedent',
      currentValue: estateBalanceSheetData.data?.principalDebtsOfDecedent,
      startingValue: null,
    },
    {
      label: 'Funeral Expenses',
      currentValue: estateBalanceSheetData.data?.principalFuneralExpenses,
      startingValue: null,
    },
    {
      label: 'Administration Expenses',
      currentValue: estateBalanceSheetData.data?.principalAdministrationExpenses,
      startingValue: null,
    },
    {
      label: 'Federal and State Taxes',
      currentValue: estateBalanceSheetData.data?.principalFederalAndStateTaxes,
      startingValue: null,
    },
    {
      label: 'Executor Commissions',
      currentValue: estateBalanceSheetData.data?.principalExecutorCommissions,
      startingValue: null,
    },
    {
      label: 'Total',
      currentValue: estateBalanceSheetData.data?.principalTotalLessDisbursements,
      startingValue: null,
    },
    {
      label: '',
      currentValue: null,
      startingValue: null,
    },
    {
      label: 'Bal Before Distribution',
      startingValue: estateBalanceSheetData.data?.principalBalanceBeforeDistributions,
      currentValue: null,
    },
    {
      label: 'Distributions to Beneficiaries',
      startingValue: estateBalanceSheetData.data?.principalDistributionsToBeneficiaries,
      currentValue: null,
    },
    {
      label: 'Principal Balance on Hand',
      startingValue: estateBalanceSheetData.data?.principalBalanceOnHand,
      currentValue: null,
    },
  ];

  const incomeRows: AccountSummaryTableRow[] = [
    {
      label: 'Receipts',
      currentValue: null,
      startingValue: estateBalanceSheetData.data?.incomeInitialValueReceipts,
    },
    {
      label: 'Less Disbursements',
      currentValue: null,
      startingValue: null,
    },
    {
      label: 'Debts of Decedent',
      startingValue: estateBalanceSheetData.data?.incomeDebtsOfDecedent,
      currentValue: null,
    },
    {
      label: 'Funeral Expenses',
      startingValue: estateBalanceSheetData.data?.incomeFuneralExpenses,
      currentValue: null,
    },
    {
      label: 'Administration Expenses',
      startingValue: estateBalanceSheetData.data?.incomeAdministrationExpenses,
      currentValue: null,
    },
    {
      label: 'Federal and State Taxes',
      startingValue: estateBalanceSheetData.data?.incomeFederalAndStateTaxes,
      currentValue: null,
    },
    {
      label: 'Executor Commissions',
      startingValue: estateBalanceSheetData.data?.incomeExecutorCommissions,
      currentValue: null,
    },
    {
      label: 'Total',
      startingValue: estateBalanceSheetData.data?.incomeTotalLessDisbursements,
      currentValue: null,
    },
    {
      label: '',
      currentValue: null,
      startingValue: null,
    },
    {
      label: 'Bal Before Distribution',
      startingValue: estateBalanceSheetData.data?.incomeBalanceBeforeDistributions,
      currentValue: null,
    },
    {
      label: 'Distributions to Beneficiaries',
      startingValue: estateBalanceSheetData.data?.incomeDistributionsToBeneficiaries,
      currentValue: null,
    },
    {
      label: 'Income Balance on Hand',
      startingValue: estateBalanceSheetData.data?.incomeBalanceOnHand,
      currentValue: null,
    },
  ];

  const totalsRows: AccountSummaryTableRow[] = [
    {
      label: 'Combined balance on hand',
      currentValue: estateBalanceSheetData.data?.combinedBalanceOnHand,
      startingValue: null,
    },
    {
      label: 'Proposed Distributions to Beneficiaries',
      currentValue: estateBalanceSheetData.data?.proposedDistributionsToBeneficiaries,
      startingValue: null,
      editColumnName: 'proposedDistributionsToBeneficiaries',
    },
  ];

  const handleDownloadCSV = () => {
    downloadCSV({ principalRows, incomeRows, totalsRows });
  };

  return (
    <HomeLayout>
      <Fade in={true}>
        <div className={classes.container}>
          <EstateBalanceSheetDashboardHeader estate={estate} pageTitle="Accounting Summary" />
          <EstateBalanceSheetDashboardMenu
            estate={estate}
            estateBalanceSheet={estateBalanceSheet}
            page="estate-accounting-summary"
          />
          <div className={classes.subTitleContainer}>
            <Typography variant="h2" className={classes.subTitle}>
              Principal
            </Typography>
            <Button
              className={classes.downloadButton}
              startIcon={<FileDownload />}
              onClick={handleDownloadCSV}
            >
              Export
            </Button>
          </div>

          <AccountSummaryTableComponent
            rows={principalRows}
            onEditButtonClick={setShowEditModalRow}
          />
          <Typography variant="h2" className={classes.subTitle}>
            Income
          </Typography>
          <AccountSummaryTableComponent rows={incomeRows} onEditButtonClick={setShowEditModalRow} />
          <Typography variant="h2" className={classes.subTitle}>
            Totals
          </Typography>
          <AccountSummaryTableComponent rows={totalsRows} onEditButtonClick={setShowEditModalRow} />
          {showEditModalRow?.editColumnName && (
            <EditAccountSummaryFieldModal
              portalId={portalId}
              column={showEditModalRow.editColumnName}
              title={showEditModalRow.label}
              showModal={!!showEditModalRow}
              onClose={() => setShowEditModalRow(undefined)}
              currentValue={estateBalanceSheetData.data?.[showEditModalRow.editColumnName]}
              estateBalanceSheetId={estateBalanceSheetData.data?.id}
            />
          )}
        </div>
      </Fade>
    </HomeLayout>
  );
};

export default EstateAccountSummaryPage;
